<template>
  <!-- Call Notes Modal -->
  <div 
    v-if="visible"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    @click.self="close"
  >
    <div class="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden">
      <!-- Header -->
      <div class="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">
            Anruf bearbeiten
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400">
            {{ callerInfo }} • {{ callTime }}
          </p>
        </div>
        <button 
          @click="close"
          class="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          <svg class="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
        <!-- Status -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Status
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="s in statusOptions"
              :key="s.value"
              @click="updateStatus(s.value)"
              :class="[
                'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                status === s.value
                  ? s.activeClass
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
              ]"
            >
              {{ s.icon }} {{ s.label }}
            </button>
          </div>
        </div>

        <!-- Assignment (only for queue calls) -->
        <div v-if="queueEmail">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Zuweisen an
          </label>
          <div class="relative">
            <select
              v-model="assignedToId"
              @change="handleAssignment"
              class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="">Nicht zugewiesen</option>
              <option 
                v-for="member in queueMembers" 
                :key="member.member_user_id"
                :value="member.member_user_id"
              >
                {{ member.member_name }}
              </option>
            </select>
            <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <p v-if="assignedToName && assignedByName" class="mt-1 text-xs text-slate-500">
            Zugewiesen von {{ assignedByName }} am {{ formatDate(assignedAt) }}
          </p>
        </div>

        <!-- Notes -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Notizen
          </label>
          <textarea
            v-model="notes"
            rows="4"
            placeholder="Notizen zum Anruf..."
            class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          ></textarea>
        </div>

        <!-- History -->
        <div v-if="history.length > 0">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Verlauf
          </label>
          <div class="space-y-2">
            <div 
              v-for="entry in history" 
              :key="entry.id"
              class="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400"
            >
              <span class="flex-shrink-0">
                {{ historyIcon(entry.action) }}
              </span>
              <span class="flex-1">
                {{ historyText(entry) }}
              </span>
              <span class="flex-shrink-0 text-slate-400">
                {{ formatDate(entry.changed_at) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
        <button
          @click="close"
          class="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          Abbrechen
        </button>
        <button
          @click="save"
          :disabled="saving"
          class="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <svg v-if="saving" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Speichern
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { authenticatedFetch } from '../oauth2-config';

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  call: {
    type: Object,
    default: null
  },
  queueEmail: {
    type: String,
    default: ''
  },
  queueName: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'saved']);

// Form state
const status = ref('open');
const notes = ref('');
const assignedToId = ref('');
const assignedToName = ref('');
const assignedByName = ref('');
const assignedAt = ref(null);
const history = ref([]);
const queueMembers = ref([]);
const saving = ref(false);
const noteId = ref(null);

// Status options
const statusOptions = [
  { value: 'open', label: 'Offen', icon: '📋', activeClass: 'bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-white' },
  { value: 'in_progress', label: 'In Bearbeitung', icon: '🔄', activeClass: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' },
  { value: 'completed', label: 'Erledigt', icon: '✅', activeClass: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' },
  { value: 'forwarded', label: 'Weitergeleitet', icon: '↗️', activeClass: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' }
];

// Computed display info
const callerInfo = computed(() => {
  if (!props.call) return '';
  return props.call.organizer_name || props.call.external_contact_name || props.call.external_phone || 'Unbekannt';
});

const callTime = computed(() => {
  if (!props.call?.start_time) return '';
  const date = new Date(props.call.start_time);
  return date.toLocaleString('de-DE', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin'
  });
});

// Load data when modal opens
watch(() => props.visible, async (newVal) => {
  if (newVal && props.call) {
    await loadNotes();
    await loadQueueMembers();
  }
}, { immediate: true });

// Load existing notes
async function loadNotes() {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/calls/${props.call.id}/notes`);
    if (response.ok) {
      const data = await response.json();
      if (data.length > 0) {
        const note = data[0];
        noteId.value = note.id;
        status.value = note.status || 'open';
        notes.value = note.notes || '';
        assignedToId.value = note.assigned_to_id || '';
        assignedToName.value = note.assigned_to_name || '';
        assignedByName.value = note.assigned_by_name || '';
        assignedAt.value = note.assigned_at;
        history.value = note.history || [];
      } else {
        // Reset form for new note
        noteId.value = null;
        status.value = 'open';
        notes.value = '';
        assignedToId.value = '';
        assignedToName.value = '';
        assignedByName.value = '';
        assignedAt.value = null;
        history.value = [];
      }
    }
  } catch (error) {
    console.error('Error loading call notes:', error);
  }
}

// Load queue members for assignment dropdown
async function loadQueueMembers() {
  if (!props.queueEmail) return;
  
  try {
    const response = await authenticatedFetch(`${API_URL}/api/calls/queues/${encodeURIComponent(props.queueEmail)}/members`);
    if (response.ok) {
      queueMembers.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading queue members:', error);
  }
}

// Update status immediately
async function updateStatus(newStatus) {
  status.value = newStatus;
}

// Handle assignment selection
function handleAssignment() {
  const selected = queueMembers.value.find(m => m.member_user_id === assignedToId.value);
  assignedToName.value = selected?.member_name || '';
}

// Save notes
async function save() {
  if (!props.call) return;
  
  saving.value = true;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/calls/${props.call.id}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        notes: notes.value,
        status: status.value,
        assignedToId: assignedToId.value || null,
        assignedToName: assignedToName.value || null,
        assignedToEmail: queueMembers.value.find(m => m.member_user_id === assignedToId.value)?.member_email || null,
        queueEmail: props.queueEmail,
        queueName: props.queueName
      })
    });

    if (response.ok) {
      const result = await response.json();
      emit('saved', result);
      close();
    } else {
      console.error('Failed to save notes');
    }
  } catch (error) {
    console.error('Error saving notes:', error);
  } finally {
    saving.value = false;
  }
}

// Close modal
function close() {
  emit('close');
}

// Format date for display
function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('de-DE', { 
    day: '2-digit', 
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin'
  });
}

// History entry icon
function historyIcon(action) {
  const icons = {
    'created': '📝',
    'updated': '✏️',
    'status_changed': '🔄',
    'assigned': '👤',
    'completed': '✅'
  };
  return icons[action] || '•';
}

// History entry text
function historyText(entry) {
  const actionTexts = {
    'created': `${entry.changed_by_name} hat die Notiz erstellt`,
    'updated': `${entry.changed_by_name} hat die Notiz bearbeitet`,
    'status_changed': `${entry.changed_by_name} hat Status auf "${statusLabel(entry.new_value)}" geändert`,
    'assigned': `${entry.changed_by_name} hat an ${entry.new_value || 'niemanden'} zugewiesen`,
    'completed': `${entry.changed_by_name} hat als erledigt markiert`
  };
  return actionTexts[entry.action] || `${entry.changed_by_name}: ${entry.action}`;
}

// Get status label
function statusLabel(value) {
  const option = statusOptions.find(o => o.value === value);
  return option?.label || value;
}
</script>
